import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type { MatchPublic, MatchStage } from '@mondy/shared';

export const STAGE_LABELS: Record<MatchStage, string> = {
  group: 'Group stage',
  round_of_32: 'Round of 32',
  round_of_16: 'Round of 16',
  quarter: 'Quarterfinal',
  semi: 'Semifinal',
  third_place: 'Third place',
  final: 'Final',
};

export const STAGE_ORDER: MatchStage[] = [
  'group',
  'round_of_32',
  'round_of_16',
  'quarter',
  'semi',
  'third_place',
  'final',
];

/**
 * Best-effort browser timezone. Falls back to UTC on exotic runtimes where
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` is unavailable.
 */
export function localTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function formatKickoff(isoUtc: string, tz: string = localTimezone()): string {
  return formatInTimeZone(isoUtc, tz, 'EEE d MMM, HH:mm zzz');
}

export function formatKickoffShort(isoUtc: string, tz: string = localTimezone()): string {
  return formatInTimeZone(isoUtc, tz, 'HH:mm');
}

export function formatKickoffDateShort(isoUtc: string, tz: string = localTimezone()): string {
  return formatInTimeZone(isoUtc, tz, 'd MMM');
}

export function formatKickoffDayMonth(isoUtc: string, tz: string = localTimezone()): string {
  return formatInTimeZone(isoUtc, tz, 'dd/MM');
}

export function formatKickoffMobileDetail(isoUtc: string, tz: string = localTimezone()): string {
  return `${formatInTimeZone(isoUtc, tz, 'EEE MMM d').toUpperCase()} · ${formatKickoffShort(
    isoUtc,
    tz,
  )} LOCAL`;
}

export function formatDayHeading(isoUtc: string, tz: string = localTimezone()): string {
  return formatInTimeZone(isoUtc, tz, 'EEEE, d MMMM');
}

export function dayKey(isoUtc: string, tz: string = localTimezone()): string {
  return formatInTimeZone(isoUtc, tz, 'yyyy-MM-dd');
}

export function isKickoffToday(isoUtc: string, tz: string = localTimezone()): boolean {
  return dayKey(isoUtc, tz) === formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');
}

export function matchLabel(m: MatchPublic): { t1: string; t2: string } {
  return {
    t1: m.team1?.name ?? m.team1Placeholder ?? 'TBD',
    t2: m.team2?.name ?? m.team2Placeholder ?? 'TBD',
  };
}

export function matchFifa(m: MatchPublic): { t1: string; t2: string } {
  return {
    t1: m.team1?.fifaCode ?? m.team1Placeholder ?? '—',
    t2: m.team2?.fifaCode ?? m.team2Placeholder ?? '—',
  };
}

export function matchHasResolvedTeams(m: MatchPublic): boolean {
  return Boolean(m.team1 && m.team2);
}

export { format };
